import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node"
import { json } from "@remix-run/node"
import { useLoaderData, useNavigate, useLocation, useRouteLoaderData } from "@remix-run/react"
import Header from "~/components/header"
import { Public } from "~/utils/remix"
import defaultAvatar from "~/assets/default-avatar.webp"
import { MagnifyingGlassIcon } from "@radix-ui/react-icons"
import { Input } from "~/components/ui/input"
import { useState, useEffect } from "react"
import { useDebounce } from "~/utils/debounce"
import { StarCard } from "~/components/cards"
import { Filtering } from "~/components/icons"
import { Button } from "~/components/ui/button"
import { getImage } from "~/utils/image.server"
import { InfiniteScroll } from "~/components/pagination"
import { StarsFilteringModal } from "~/components/makelovenotpornstars/stars-filtering-modal"
import type { UrlFilterParamsMap } from "~/utils/filter-sort-utils"
import {
  useFilters,
  getUserLocations,
  useUpdateSortAndFilterParams,
  useUpdateSortParam,
  parseUrlParams,
  applySortOption,
  applyFilters,
  mapUrlFilterParamsToFilterOptions,
} from "~/utils/filter-sort-utils"
import { UserAttributes } from "~/utils/data"
import type { SortOption } from "~/components/makelovenotpornstars/stars-sorting-menu"
import { starsSortingOptions, StarsSortingMenu } from "~/components/makelovenotpornstars/stars-sorting-menu"
import { Prisma } from "@prisma/client"

const pageCount = 30

const buildWhereClause = (filters: UrlFilterParamsMap) => {
  const whereClause: any = {
    Status: 1,
    Deleted: 0,
    Role: 0,
    Private: { not: 1 },
    Videos: {
      some: { Status: 1, Deleted: 0 },
    },
  }

  const andConditions = Object.entries(filters)
    .filter(([filterType, filterValues]) => filterValues.length > 0)
    .map(([filterType, filterValues]) => {
      return {
        UserAttributeTags: {
          some: {
            OR: filterValues.map((value) => ({
              Tag: {
                Name: value,
              },
            })),
          },
        },
      }
    })

  if (andConditions.length > 0) {
    whereClause.AND = andConditions
  }
  return whereClause
}

export const loader = Public(async ({ request, context }: LoaderFunctionArgs) => {
  const db = context.db

  const url = new URL(request.url)
  let page = url.searchParams.get("page") || "1"
  const locations = await getUserLocations(db)
  const completeUserAttributes = UserAttributes.map((attr) => {
    if (attr.value === "location") {
      return { ...attr, options: locations }
    }
    return attr
  })
  const { parsedFilters, parsedSortOption } = parseUrlParams(url.search)
  const whereClause = buildWhereClause(parsedFilters)
  let orderBy
  switch (parsedSortOption) {
    case "newest":
      orderBy = { CreatedAt: Prisma.SortOrder.desc }
      break
    // TODO: Implement the correct logic for "recently-active" sorting
    // case "recently-active":
    // 	orderBy = { UpdatedAt: Prisma.SortOrder.desc };
    // 	break;
    case "z-a":
      orderBy = { DisplayName: Prisma.SortOrder.desc }
      break
    case "a-z":
    default:
      orderBy = { DisplayName: Prisma.SortOrder.asc }
      break
  }

  const stars = await db.users.findMany({
    where: whereClause,
    select: {
      UserName: true,
      DisplayName: true,
      Avatar: true,
      UserID: true,
      IsSFW: true,
    },
    orderBy: orderBy,
    take: pageCount,
    skip: (parseInt(page) - 1) * pageCount,
  })

  return json({
    loggedIn: !!context.user,
    items: stars.map((star) => {
      return {
        ...star,
        DisplayName: star.DisplayName,
        Avatar: getImage(star.Avatar, defaultAvatar),
      }
    }),
    completeUserAttributes,
  })
})

export const meta: MetaFunction = () => {
  return [
    { title: "Browse Make Love Not Pornstars" },
    {
      property: "og:title",
      content: "Browse Make Love Not Pornstars",
    },
    {
      name: "description",
      content:
        "These real people share their homemade real sex tapes. Browse the hundreds of MakeLoveNotPornstars waiting for you – come see their real world sex lives!",
    },
  ]
}

export default function Stars() {
  const data = useLoaderData<typeof loader>()
  const rootData: any = useRouteLoaderData("root")
  const navigate = useNavigate()
  const location = useLocation()
  const [items, setItems] = useState(data.items)
  const [term, setTerm] = useState("")
  const { filters, setFilter, handleClearAll } = useFilters()
  const [isModalOpen, setModalOpen] = useState(false)
  const [sortOption, setSortOption] = useState<SortOption>(starsSortingOptions[0]) // default value: a-z
  const updateUrlParams = useUpdateSortAndFilterParams()
  const updateSortParam = useUpdateSortParam()

  useEffect(() => {
    setItems(data.items)
  }, [data.items])

  const searchQuery = useDebounce(() => {
    const currentParams = new URLSearchParams(location.search)
    navigate(`/makelovenotpornstars/search/${term}?${currentParams.toString()}`, { preventScrollReset: true })
  }, 500)

  const onSearchChange = (e: any) => {
    setTerm(e.target.value)
    searchQuery()
  }

  useEffect(() => {
    const { parsedFilters, parsedSortOption } = parseUrlParams(location.search)

    if (!parsedSortOption && Object.keys(parsedFilters).length === 0) {
      setSortOption(starsSortingOptions[0])
      handleClearAll()
      return
    }

    const filterOptions = mapUrlFilterParamsToFilterOptions(parsedFilters)

    applySortOption(parsedSortOption, sortOption, setSortOption, starsSortingOptions)
    applyFilters(filterOptions, setFilter)
  }, [])

  useEffect(() => {
    updateSortParam(sortOption)
  }, [sortOption])

  const handleFilterSubmit = () => {
    updateUrlParams(sortOption, filters)
    setModalOpen(false)
  }

  const actionUrl = `/makelovenotpornstars?${new URLSearchParams(location.search).toString()}`

  return (
    <div className="mt-16">
      <Header title="MakeLoveNotPornstars" />

      <div className="mx-4 mb-7">
        <div className="mx-4 mb-7 flex flex-col md:flex-row md:mx-6 items-center justify-between">
          <div className="relative flex w-full mb-4 md:mb-0 md:w-1/4 min-w-[300px]">
            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search Stars"
              value={term}
              onChange={onSearchChange}
              className="pl-8 border-foreground/25"
            />
          </div>
          <div className="flex justify-between w-full md:w-auto">
            <Button onClick={() => setModalOpen(true)} className="flex items-center justify-center" variant="ghost">
              <Filtering className="h-5 w-5 text-foreground text-muted-foreground" />
              <span className="ml-2 text-foreground text-sm">Filters</span>
            </Button>
            <StarsFilteringModal
              open={isModalOpen}
              setOpen={setModalOpen}
              title="I want to find..."
              filters={filters}
              setFilter={setFilter}
              onClearAll={handleClearAll}
              onSubmit={handleFilterSubmit}
              completeUserAttributes={data.completeUserAttributes}
            />
            <StarsSortingMenu currentSortOption={sortOption} onChange={setSortOption} />
          </div>
        </div>
      </div>

      <div className="mx-4">
        <InfiniteScroll items={items} setItems={setItems} action={actionUrl} separator="&">
          <div className="flex gap-2 w-full justify-center flex-wrap">
            {items.map((star: any, index: number) => (
              <StarCard key={index} star={star} loggedIn={rootData?.loggedIn} isPaymentVerified={rootData?.isPaymentVerified} />
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  )
}
